import { type ChangeEvent, useEffect, useId, useRef, useState } from 'react';

export const DEFAULT_IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

interface UseImageUploadOptions {
  onFileSelect: (file: File) => void;
  accept?: string;
  disabled?: boolean;
  name?: string;
}

/**
 * File input plumbing behind an image upload: the hidden input, the local preview and its cleanup.
 * Lives apart from ImageUpload so a component with its own upload UI can reuse this logic.
 */
export const useImageUpload = ({
  onFileSelect,
  accept = DEFAULT_IMAGE_ACCEPT,
  disabled = false,
  name = 'avatar',
}: UseImageUploadOptions) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Two uploads on the same page would otherwise share an id when they share a name.
  const inputId = `image-upload-${name}-${useId()}`;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    onFileSelect(file);
  };

  return {
    inputId,
    previewUrl,
    open: () => {
      if (!disabled) inputRef.current?.click();
    },
    inputProps: {
      ref: inputRef,
      id: inputId,
      type: 'file' as const,
      name,
      accept,
      disabled,
      onChange: handleChange,
      className: 'sr-only',
      tabIndex: -1,
      'aria-hidden': true,
    },
  };
};
